import { GetProductCodeByWebIdQuery } from 'packages/shared/definitions/types';

import { isMobileApp } from '@notino/analyzer-tools';

import { dispatchTrackingEvent } from '@context/tracking/utils';

import {
  HAIR_START_PAGE_TITLE,
  HairPageViewPayload,
  TrackedProduct,
} from './hooks/useTracking/model';

type LoadProductCodes = (
  ids: string[]
) => Promise<GetProductCodeByWebIdQuery['vpProductByIds']>;

const getTrackingProduct = ({
  product,
  listName,
  productCode,
}: {
  product: TrackedProduct;
  productCode?: string | null;
  listName?: 'hair_anl_pdp' | 'hair_anl_list';
}) => ({
  brand_name: product.item_brand ?? '',
  full_price: product.price ?? 0,
  kind: '',
  price: product.price ?? 0,
  primary_subcategory: '',
  primary_category: product.item_category ?? '',
  primary_type: '',
  product_code: productCode ?? '',
  product_name: product.item_name ?? '',
  ...(listName && { list_name: listName }),
});

export const trackViewItem = async (
  product: TrackedProduct,
  loadProductCodes: LoadProductCodes
) => {
  if (!product.item_id) {
    return;
  }

  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
    listName: 'hair_anl_pdp',
  });

  dispatchTrackingEvent({
    event: 'view_item',
    products: [trackingProduct],
    location: 'hair_anl',
    _clear: true,
  });
};

export const trackViewItemList = async (
  products: TrackedProduct[],
  loadProductCodes: LoadProductCodes
) => {
  const identifiableProducts = products.filter((product) => !!product.item_id);

  if (!identifiableProducts.length) {
    return;
  }

  const prodCode = await loadProductCodes(
    identifiableProducts.map((p) => p.item_id as string)
  );

  const trackingProducts = identifiableProducts.map((product) => ({
    ...getTrackingProduct({
      product,
      productCode: prodCode.find((code) => code?.id === product.item_id)
        ?.productCode,
      listName: 'hair_anl_list',
    }),
    list_position: 0,
  }));

  dispatchTrackingEvent({
    event: 'view_item_list',
    product_lists: {
      list_name: 'hair_anl_list',
      interaction: 'click',
      sorting_type: 'automatic',
      products: trackingProducts,
    },
    _clear: true,
  });
};

export const trackAddToCard = async (
  product: TrackedProduct,
  loadProductCodes: LoadProductCodes
) => {
  if (!product.item_id) {
    return;
  }

  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
  });

  dispatchTrackingEvent({
    event: 'add_to_cart',
    product: {
      ...trackingProduct,
      quantity: 1,
      cart_type: 'hair_anl',
    },
    _clear: true,
  });
};

export const trackAddToWishList = async (
  product: TrackedProduct,
  timing: number,
  loadProductCodes: LoadProductCodes
) => {
  if (!product.item_id) {
    return;
  }

  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
  });

  dispatchTrackingEvent({
    event: 'add_to_wishlist',
    wishlist: {
      action: 'product_added_to_wishlist',
      interaction: 'click_add',
      type: 'hair_anl',
      timing,
      products: [trackingProduct],
    },
    _clear: true,
  });
};

export const trackRemoveFromWishList = async (
  product: TrackedProduct,
  timing: number,
  loadProductCodes: LoadProductCodes
) => {
  if (!product.item_id) {
    return;
  }

  const prodCode = await loadProductCodes([product.item_id]);

  const trackingProduct = getTrackingProduct({
    product,
    productCode: prodCode[0]?.productCode,
  });

  dispatchTrackingEvent({
    event: 'remove_from_wishlist',
    wishlist: {
      action: 'product_removed_from_wishlist',
      interaction: 'click_add',
      type: 'hair_anl',
      timing,
      products: [trackingProduct],
    },
    _clear: true,
  });
};

export const trackViewOrSelectPromotion = (
  brand: string,
  event: 'select_promotion' | 'view_promotion'
) => {
  dispatchTrackingEvent({
    event,
    promotions: [
      {
        code: 'hair_anl',
        id: brand,
        location: 'hair_anl_pdp',
        creative: '',
        destination_url: '',
        name: '',
        position: 0,
      },
    ],
    _clear: true,
  });
};

/**
 * Marks Revieve's `page_view` of the Start screen as a brand-specific Hair
 * Analyzer session opened from product detail. The payload object is mutated in
 * place on purpose: Revieve forwards the very same object to GA4 right after
 * `onTrackingEvent` returns, so this must run synchronously and must not clone.
 *
 * Mirrors extendSAPageViewTracking of the Skin Analyzer, with the flag renamed
 * to isBrandSpecificHA.
 */
export const extendHAPageViewTracking = (
  payload: HairPageViewPayload,
  brandId?: string | null
) => {
  if (payload.page_title !== HAIR_START_PAGE_TITLE || !brandId) {
    return;
  }

  payload.textValue = brandId;
  payload.isBrandSpecificHA = true;
  payload.isMobileApp = isMobileApp();
};
